// --------------------------------------------------------------
// Created On: 2024-05-29
// Author: Hannah Vaughan
//
// Last Modified: 2024-09-23
// Modified By: KC Willard
//
// Copyright 2024 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import styles from "./CompanyListItem.module.scss";
import { Collapse } from "react-bootstrap";

// List component for the CompanyDistributorPage that either displays distributor account information and deletion
// functionality for companies that are linked to a distributor or displays a search field to add an existing, unlinked
// distributor to the company.
export default function CompanyListItem(props: Props): Component {
  const [distributorFilter, setDistributorFilter] = useState<string>("");
  const distributor = props.getDistributorById(props.distributorAccountId);

  // Updates the distributorFilter whenever the entered value changes, then verifies that the value matches the name of
  // an existing distributor that is not currently linked to another company.
  function onDistributorFilterChange(value: string): void {
    setDistributorFilter(value);
    props.onChangeDistributorAccountId(value);
  }

  // Links the distributor account with the valid filter name with the current company.
  // Note that this point will only be reached after the distributor name filter has been validated,
  // and the API PUT request is only made if the name can be associated with a valid, unlinked distributor.
  function onLinkDistributorAccount(): void {
    // Only use the distributor filter for searching through distributors if it has already been validated.
    if (props.isValidDistributorName) {
      const distributorAccount = props.getDistributorByName(distributorFilter.toLowerCase());

      if (distributorAccount !== undefined) {
        props.onLinkDistributorAccount(distributorAccount.distributorAccountId);
      }
    }
    setDistributorFilter("");
  }

  // Handle the onClick even for the company list item
  function handleIsActive() {
    if (props.isActive) {
      props.onToggle(null);
    } else {
      props.onToggle(props.companyId);
    }
  }

  return (
    <li>
      <div className={styles.companyItem} id="companyListItemAccordion" data-test="company-list-item-container">
        <div className="accordion-item">
          <div className="card" onClick={() => handleIsActive()} data-test="company-list-item-header">
            <div className="container">
              <div className={`row ${styles.companyRow}`}>
                <div className={`col ${styles.companyName}`}>{props.companyName}</div>
                {props.distributorAccountId && (
                  <div className={`col ${styles.companyLink}`} data-test="company-distributor-link-icon">
                    <i className="fa fa-lg fa-link" aria-hidden="true"></i>
                  </div>
                )}
              </div>
            </div>
          </div>
          <Collapse in={props.isActive}>
            {distributor !== undefined ? (
              <div className="container">
                <div className={`row ${styles.distributorRow}`}>
                  <div className={`col ${styles.distributorName}`} data-test="distributor-name">
                    {distributor.name}
                  </div>
                  <div
                    className={`col ${styles.distributorDeleteLinkButton}`}
                    data-test="delete-distributor-link-button"
                  >
                    <button className="btn btn-danger" onClick={() => props.onDeleteLink(props.companyId)}>
                      <i className={`fa fa-times ${styles.distributorDeleteLinkButton}`} aria-hidden="true"></i>
                    </button>
                  </div>
                  {props.errorMessage.length > 0 && (
                    <div className={`row ${styles.distributorRow}`}>
                      <div className="col">
                        <p className={`${styles.errorMessage} form-text`} data-test="delete-link-error-message">
                          {props.errorMessage}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="container">
                <div className={`row ${styles.distributorRow}`} data-test="distributor-search-form">
                  <div className="col">
                    <input
                      list="datalist-options"
                      className="form-control"
                      id={`distributor-search-${props.companyId}`}
                      placeholder="Search for distributor account..."
                      value={distributorFilter}
                      onChange={(e) => onDistributorFilterChange(e.target.value)}
                      data-test="distributor-search-input"
                      type="search"
                    />
                    {props.errorMessage.length > 0 && (
                      <div className={`${styles.errorMessage} form-text`} data-test="distributor-search-error-message">
                        {props.errorMessage}
                      </div>
                    )}
                  </div>
                  <div className="col">
                    <div onClick={() => onLinkDistributorAccount()} data-test="link-company-distributor-plus-button">
                      {props.isValidDistributorName ? (
                        <button type="submit" className="btn btn-success">
                          <i className={`fa fa-plus ${styles.distributorAddLinkButton}`}></i>
                        </button>
                      ) : (
                        <button type="submit" className="btn btn-secondary" disabled>
                          <i className="fa fa-plus"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Collapse>
        </div>
      </div>
    </li>
  );
}

interface Props {
  companyId: number;
  companyName: string;
  distributorAccountId: number | null;
  errorMessage: string;
  isActive: boolean;
  isValidDistributorName: boolean;
  onToggle: (companyId: number | null) => void; // Used to update which accordion is open so that only one accordion is open at a time.
  getDistributorById: (distributorAccountId: number | null) => DistributorAccount | undefined;
  getDistributorByName: (distributorAccountName: string) => DistributorAccount | undefined;
  onChangeDistributorAccountId: (filter: string) => void; // Used to check whether the selected ID is in use by another company.
  onLinkDistributorAccount: (distributorAccountId: number) => void; // Used by the "plus sign" button to post the link to the DB.
  onDeleteLink: (companyId: number) => void; // Used by the "X" button to clear any accounts associated with the company.
}

interface DistributorAccount {
  distributorAccountId: number;
  name: string;
}
